/** Tiny in-memory ring buffer of runtime errors, for the on-device debug panel.
 *
 * The cashier runs this inside the POS web view where there's no dev console, so we capture
 * `window.onerror`, unhandled promise rejections, and `console.error` (the channel the
 * `/customer` crash surfaced on) into a buffer the DebugPanel can render. Module-level so it
 * survives React unmounts (e.g. the PostHog error boundary swapping the tree).
 */
export interface DebugLogEntry {
  id: number
  time: string
  kind: 'error' | 'unhandledrejection' | 'console.error'
  message: string
  stack?: string
}

const MAX = 50
let entries: DebugLogEntry[] = []
const listeners = new Set<() => void>()
let nextId = 1
let installed = false

function add(kind: DebugLogEntry['kind'], message: string, stack?: string): void {
  const entry: DebugLogEntry = {
    id: nextId++,
    time: new Date().toISOString().slice(11, 23),
    kind,
    message,
    stack,
  }
  // New array reference each time so subscribers re-render (entries are otherwise mutated in place).
  entries = [entry, ...entries].slice(0, MAX)
  listeners.forEach((l) => l())
}

export function getDebugLog(): DebugLogEntry[] {
  return entries
}

export function subscribeDebugLog(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function clearDebugLog(): void {
  entries = []
  listeners.forEach((l) => l())
}

function stringifyArg(a: unknown): string {
  if (a instanceof Error) return a.message
  if (typeof a === 'string') return a
  try {
    return JSON.stringify(a)
  } catch {
    return String(a)
  }
}

/** Install the global capture hooks. Idempotent — safe to call more than once. */
export function installDebugCapture(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (e) => {
    add('error', e.message || String(e.error), e.error instanceof Error ? e.error.stack : undefined)
  })

  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason
    add(
      'unhandledrejection',
      r instanceof Error ? r.message : String(r),
      r instanceof Error ? r.stack : undefined,
    )
  })

  const original = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    const err = args.find((a) => a instanceof Error) as Error | undefined
    add('console.error', args.map(stringifyArg).join(' '), err?.stack)
    original(...args)
  }
}
